import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import ApiService from '../services/api';

const sanitizePrivacyPolicyContent = (html: string): string => {
  return html
    .replace(/max-width\s*:\s*[^;]+;?/gi, '')
    .replace(/width\s*:\s*[^;]+;?/gi, '')
    .replace(/height\s*:\s*[^;]+;?/gi, '')
    .replace(/padding\s*:\s*[^;]+;?/gi, '')
    .replace(/padding-top\s*:\s*[^;]+;?/gi, '')
    .replace(/padding-bottom\s*:\s*[^;]+;?/gi, '')
    .replace(/padding-left\s*:\s*[^;]+;?/gi, '')
    .replace(/padding-right\s*:\s*[^;]+;?/gi, '')
    .replace(/margin\s*:\s*[^;]+;?/gi, '')
    .replace(/margin-top\s*:\s*[^;]+;?/gi, '')
    .replace(/margin-bottom\s*:\s*[^;]+;?/gi, '')
    .replace(/margin-left\s*:\s*[^;]+;?/gi, '')
    .replace(/margin-right\s*:\s*[^;]+;?/gi, '')
    .replace(/font-family\s*:\s*[^;]+;?/gi, '')
    .replace(/font-size\s*:\s*[^;]+;?/gi, '')
    .replace(/color\s*:\s*[^;]+;?/gi, '')
    .replace(/text-decoration\s*:\s*[^;]+;?/gi, '')
    .replace(/line-height\s*:\s*[^;]+;?/gi, '')
    .replace(/border-left\s*:\s*[^;]+;?/gi, '')
    .replace(/border-left-width\s*:\s*[^;]+;?/gi, '')
    .replace(/border-left-style\s*:\s*[^;]+;?/gi, '')
    .replace(/border-left-color\s*:\s*[^;]+;?/gi, '')
    .replace(/border\s*:\s*[^;]+;?/gi, '')
    .replace(/border-top\s*:\s*[^;]+;?/gi, '')
    .replace(/border-right\s*:\s*[^;]+;?/gi, '')
    .replace(/border-bottom\s*:\s*[^;]+;?/gi, '')
    .replace(/\s+border\s*=\s*["'][^"']*["']/gi, '')
    .replace(/list-style\s*:\s*[^;]+;?/gi, '')
    .replace(/list-style-type\s*:\s*[^;]+;?/gi, '')
    .replace(/style\s*=\s*["'][^"']*max-width[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*width[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*height[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*padding[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*margin[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*font-family[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*font-size[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*color[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*text-decoration[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*line-height[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*border-left[^"']*["']/gi, '')
    .replace(/style\s*=\s*["'][^"']*border[^"']*["']/gi, '')
    .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
    .replace(/<(\w+)[^>]*>\s*&nbsp;\s*<\/\1>/gi, '')
    .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
    .replace(/<(\w+)[^>]*>\s*&nbsp;\s*<\/\1>/gi, '')
    .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/<\/ol>\s*<ol\b[^>]*>/gi, '')
    .replace(/<\/ul>\s*<ul\b[^>]*>/gi, '')
    .replace(/(<li\b[^>]*>)(\s*(?:<(?:p|span|div)[^>]*>)?\s*)\d+\.\s+/gi, '$1$2')
    .replace(/(<li\b[^>]*>)(\s*(?:<(?:p|span|div)[^>]*>)?\s*)\|+\s*•?\s*/gi, '$1$2')
    .replace(/(<li\b[^>]*>)(\s*(?:<(?:p|span|div)[^>]*>)?\s*)•\s*/gi, '$1$2')
    .replace(/(<li\b[^>]*>)(\s*(?:<(?:p|span|div)[^>]*>)?\s*)[-–—]\s+/gi, '$1$2')
    .replace(/(<p\b[^>]*>)\s*\|+\s*•?\s*/gi, '$1')
    .replace(/(<p\b[^>]*>)\s*•\s*/gi, '$1')
    .replace(/(<p\b[^>]*>)\s*[-–—]\s+/gi, '$1')
    .trim();
};

const PRIVACY_POLICY_IFRAME_STYLES = `
  @font-face {
    font-family: 'HerbalifeFont';
    src: url('/fonts/HerbalifeNatural-Light.otf') format('opentype');
    font-weight: 300;
  }
  @font-face {
    font-family: 'HerbalifeFont';
    src: url('/fonts/HerbalifeNatural-Regular.otf') format('opentype');
    font-weight: 400;
  }
  @font-face {
    font-family: 'HerbalifeFont';
    src: url('/fonts/HerbalifeNatural-Bold.otf') format('opentype');
    font-weight: 700;
  }
  * { box-sizing: border-box; }
  html, body {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    margin: 0;
    padding: 0;
  }
  body {
    font-family: 'HerbalifeFont', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    line-height: 1.6;
    color: #2f2f2f;
    background: #fff;
    font-size: 14px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  h1, h2, h3, h4, h5, h6 {
    color: #2f2f2f;
    margin: 0 0 1rem;
    font-weight: 600;
  }
  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.3rem; }
  h3 { font-size: 1.2rem; }
  p, li, span, div {
    margin-bottom: 1rem;
    line-height: 1.6;
    text-align: justify;
    word-wrap: break-word;
    overflow-wrap: break-word;
    border: none !important;
    border-left: none !important;
    border-left-style: none !important;
    border-left-width: 0 !important;
  }
  ul, ol {
    margin: 0 0 1rem;
    padding: 0 0 0 1.5rem;
    list-style-position: outside;
    border: none !important;
    border-left: none !important;
  }
  ul {
    list-style-type: disc;
  }
  ol {
    list-style-type: decimal;
  }
  ul li {
    list-style-type: disc;
  }
  ol li {
    list-style-type: decimal;
  }
  ol ol {
    list-style-type: lower-alpha;
  }
  ul ul {
    list-style-type: circle;
  }
  li {
    display: list-item;
    margin-bottom: 0.75rem;
    border: none !important;
    border-left: none !important;
    text-align: justify;
    line-height: 1.6;
    padding-left: 0.25em;
  }
  li > p,
  li > span,
  li > div {
    display: inline;
    margin: 0;
    padding: 0 !important;
    border: none !important;
    text-align: inherit;
  }
  ul ul,
  ol ol,
  ul ol,
  ol ul {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    border: none !important;
    border-left: none !important;
  }
  a {
    color: #2f2f2f;
    text-decoration: underline;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
    font-size: 14px;
    margin: 1rem 0;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fff;
  }
  th, td {
    width: 1%;
    padding: 10px 12px;
    text-align: left;
    vertical-align: top;
    border: 1px solid #e0e0e0;
    line-height: 1.5;
    word-wrap: break-word;
    overflow-wrap: break-word;
    color: #2f2f2f;
  }
  th {
    background-color: #2f2f2f;
    color: #fff;
    font-weight: 600;
    border-color: #2f2f2f;
  }
  thead th {
    background-color: #2f2f2f;
    color: #fff;
  }
  tbody tr:nth-child(even) td {
    background-color: #fafafa;
  }
  @media (max-width: 768px) {
    body {
      font-size: 13px;
      padding: 0 4px;
      -webkit-text-size-adjust: 100%;
    }
    h1 { font-size: 1.15rem; }
    h2 { font-size: 1.05rem; }
    h3 { font-size: 1rem; }
    p, li, span {
      font-size: 13px;
      line-height: 1.5;
      text-align: left;
      margin-bottom: 0.75rem;
    }
    li {
      text-align: left;
      margin-bottom: 0.65rem;
    }
    ul, ol {
      padding-left: 1.25rem;
      margin-bottom: 0.75rem;
    }
    table {
      display: block;
      width: 100%;
      max-width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      border-radius: 4px;
      margin: 0.75rem 0;
    }
    table thead,
    table tbody,
    table tfoot {
      display: table;
      width: 100%;
      min-width: 520px;
    }
    table tr {
      display: table-row;
    }
    th, td {
      display: table-cell;
      width: 1%;
      min-width: 90px;
      padding: 8px;
      font-size: 12px;
      line-height: 1.4;
    }
    th {
      font-size: 11px;
      background-color: #2f2f2f;
      color: #fff;
      border-color: #2f2f2f;
    }
  }
`;

const PrivacyPoliciesPage: React.FC = () => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const { 
    sessionId, 
    token, 
    fetchPrivacyPolicyTranslations, 
    // getPrivacyPolicyTranslation,
    privacyPolicyTranslationData, 
    getTranslation,
    currentLanguage
  } = useAppContext();

  // Function to resize iframe based on content
  const resizeIframe = (iframe: HTMLIFrameElement) => {
    try {
      if (iframe.contentWindow?.document) {
        const doc = iframe.contentWindow.document;
        const height = Math.max(
          doc.body?.scrollHeight ?? 0,
          doc.documentElement?.scrollHeight ?? 0
        );
        iframe.style.height = `${height + 8}px`;
      }
    } catch (error) {
      console.error('Error resizing iframe:', error);
      iframe.style.height = '70vh';
    }
  };

  // Store the current view when component mounts if no previous view is stored
  useEffect(() => {
    const storedPrevView = sessionStorage.getItem('privacyPolicyPrevView');
    const selectedPolicy = sessionStorage.getItem('selectedPolicy');
    
    if (!storedPrevView) {
      // If no previous view is stored, default to home
      sessionStorage.setItem('privacyPolicyPrevView', 'home');
    }

    // Auto-expand the selected policy accordion if one was specified
    if (selectedPolicy) {
      setExpandedAccordion(selectedPolicy);
    }
  }, []);

  // Clean up selectedPolicy from sessionStorage when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('selectedPolicy');
    };
  }, []);

  // Fetch privacy policy translations when component mounts
  useEffect(() => {
    const loadPrivacyPolicyTranslations = async () => {
      try {
        const apiService = new ApiService(sessionId, token);
        // Use language from global context
        const selectedLanguage = currentLanguage || 'es';
        await fetchPrivacyPolicyTranslations(apiService, selectedLanguage);
      } catch (error) {
        console.error('Failed to load privacy policy translations:', error);
      }
    };

    loadPrivacyPolicyTranslations();
  }, [sessionId, token, fetchPrivacyPolicyTranslations]);


  // Don't clean up stored previous view - let it persist for navigation
  // The view will be cleaned up when the user actually navigates away via the back button

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
    if (isExpanded) {
      window.setTimeout(() => {
        document.querySelectorAll('iframe').forEach((node) => {
          resizeIframe(node as HTMLIFrameElement);
        });
      }, 150);
    }
  };

  const createFullHtmlContent = (htmlContent: string | undefined, fallbackContent: string) => {
    let content = htmlContent || fallbackContent;

    if (content) {
      content = sanitizePrivacyPolicyContent(content);
    }

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>${PRIVACY_POLICY_IFRAME_STYLES}</style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;
  };

  const privacyPolicies = [
    {
      id: 'distributor-privacy-terms',
      title: getTranslation('Txt855'),
      type: 'iframe',
      content: createFullHtmlContent(
        privacyPolicyTranslationData?.privacy_policy?.distributor_privacy_policy?.content_html,
        '<p>Distributor Privacy Terms content is loading...</p>'
      )
    },
    {
      id: 'orbo-privacy',
      title:  getTranslation('Txt853'),
      type: 'iframe',
      content: createFullHtmlContent(
        privacyPolicyTranslationData?.privacy_policy?.orbo_privacy_policy?.content_html,
        '<p>Orbo Privacy Policy content is loading...</p>'
      )
    },
    {
      id: 'herbalife-privacy',
      title: getTranslation('Txt871'),
      type: 'content',
      content: (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'var(--color-text-primary)' }}>
           <a style={{
            color: 'var(--color-text-primary)',
            textDecoration: 'underline',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
           }} href="https://www.herbalife.com/en-us/footer/privacy-policy-by-country" target="_blank" rel="noopener noreferrer"> https://www.herbalife.com/en-us/footer/privacy-policy-by-country</a>  
          </Typography>
        </Box>
      )
    },

    {
      id: 'data-privacy-terms',
      title: getTranslation('Txt867'),
      type: 'iframe',
      content: createFullHtmlContent(
        privacyPolicyTranslationData?.privacy_policy?.data_privacy_terms?.content_html,
        '<p>Data Privacy Terms content is loading...</p>'
      )
    }
  ];

  return (
    <Layout showBackButton={true}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'var(--color-background)',
          width: '100%',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              mb: { xs: 1, sm: 2 },
              gap: { xs: 0.5, sm: 1.5 },
              width: '100%',
              maxWidth: { xs: '100%', sm: 'auto' }
            }}
          >
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                color: 'var(--color-text-primary)', 
                fontSize: { xs: '1.2rem', sm: '2.5rem', md: '3.5rem' }, 
                lineHeight: { xs: 1.2, sm: 1.1, md: 1 },
                textAlign: 'center',
                wordBreak: 'break-word'
              }}
            >
         {getTranslation('Txt849')}
              </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 400,
                color: 'var(--color-text-primary)',
                fontSize: { xs: '1.2rem', sm: '2.5rem', md: '3.5rem' },
                lineHeight: { xs: 1.2, sm: 1.1, md: 1 },
                textAlign: 'center',
                wordBreak: 'break-word'
              }}
            >
              {getTranslation('Txt850')}
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: 'var(--color-text-primary)',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            {getTranslation('Txt851')}
          </Typography>
        </Box>

        {/* Privacy Policy Accordions */}
        <Box sx={{ backgroundColor: 'var(--color-background)', overflow: 'hidden' }}>
          {privacyPolicies.map((policy, index) => (
            <React.Fragment key={policy.id}>
              <Accordion
                expanded={expandedAccordion === policy.id}
                onChange={handleAccordionChange(policy.id)}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: 0,
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: expandedAccordion === policy.id ? '#f0f0f0' : 'var(--color-background)',
                    borderBottom: '1px solid var(--color-border)',
                    minHeight: { xs: '64px', sm: '80px' },
                    height: { xs: 'auto', sm: '80px' },
                    py: { xs: 1, sm: 0 },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-expanded': {
                      backgroundColor: '#f0f0f0',
                      minHeight: { xs: '64px', sm: '80px' },
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      color: 'var(--color-text-primary)',
                      fontSize: { xs: '1rem', md: '1.1rem' }
                    }}
                  >
                    {policy.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: { xs: 0.5, sm: 2, md: 3 },
                    px: { xs: 0, sm: 2, md: 3 },
                    backgroundColor: 'var(--color-background)',
                    overflow: 'hidden',
                  }}
                >
                  {policy.type === 'iframe' ? (
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        mx: { xs: 0, sm: 0 },
                      }}
                    >
                      <iframe
                        srcDoc={typeof policy.content === 'string' ? policy.content : ''}
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          border: 'none',
                          height: '0',
                          display: 'block',
                        }}
                        title={policy.title}
                        onLoad={(e) => resizeIframe(e.target as HTMLIFrameElement)}
                      />
                    </Box>
                  ) : (
                    policy.content
                  )}
                </AccordionDetails>
              </Accordion>
              {index < privacyPolicies.length - 1 && (
                <Divider sx={{ borderColor: 'var(--color-border)' }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Layout>
  );
};

export default PrivacyPoliciesPage;
